import type { DashboardWidgetDefinition, DashboardWidgetPlacement } from '../widget-and-graphs.models';
import { teamDashboard } from './team';
import { departmentDashboard } from './department';
import { accessRoleDashboard } from './access-role';
import { WIDGET_DEFINITIONS, widgetsForApp } from '../widgets/index';
import { homeDashboard } from './home';
import { crmDashboard } from './crm';
import { salesDashboard } from './sales';
import { procurementDashboard } from './procurement';
import { inventoryDashboard } from './inventory';
import { financeDashboard } from './finance';
import { expensesDashboard } from './expenses';
import { rentalDashboard } from './rental';
import { subscriptionsDashboard } from './subscriptions';
import { pointOfSaleDashboard } from './point-of-sale';
import { branchDashboard } from './branch';
import { locationDashboard } from './location';
import { organisationDashboard } from './organisation';
import { financeRecordDashboard } from './finance-record';
import { storageLocationDashboard } from './storage-location';
import { bankAccountDashboard } from './bank-account';
import { resolveDashboardPlacements } from './dashboard-definition';

export * from './dashboard-definition';
export { homeDashboard, crmDashboard, salesDashboard, procurementDashboard, inventoryDashboard, financeDashboard,
  expensesDashboard, rentalDashboard, subscriptionsDashboard, pointOfSaleDashboard, branchDashboard, locationDashboard,
  organisationDashboard, financeRecordDashboard, storageLocationDashboard, bankAccountDashboard };

export const DEFAULT_DASHBOARDS = {
  home: homeDashboard,
  crm: crmDashboard,
  sales: salesDashboard,
  procurement: procurementDashboard,
  inventory: inventoryDashboard,
  finance: financeDashboard,
  expenses: expensesDashboard,
  rental: rentalDashboard,
  subscriptions: subscriptionsDashboard,
  'point-of-sale': pointOfSaleDashboard,
} as const;

export function appDefaultDashboardPlacements(appId: string) {
  const dashboard = DEFAULT_DASHBOARDS[appId as keyof typeof DEFAULT_DASHBOARDS] ?? homeDashboard;
  const available = new Set(widgetsForApp(appId).map(widget => widget.widgetId));
  return resolveDashboardPlacements(dashboard).filter(item => available.has(item.widgetId));
}

/** Compatibility entry points use the same object resolver as new dashboard consumers. */
export function defaultDashboardPlacements(appId: string) { return appDefaultDashboardPlacements(appId); }
export function organisationDashboardPlacements() { return resolveDashboardPlacements(organisationDashboard); }
export function financeRecordDashboardPlacements() { return resolveDashboardPlacements(financeRecordDashboard); }
export function bankAccountDashboardPlacements(recordId: string, inputs: Record<string, unknown>) {
  return resolveDashboardPlacements(bankAccountDashboard, { recordId, inputs });
}
export function branchDashboardPlacements(recordId: string, isOutlet: boolean) {
  return resolveDashboardPlacements(branchDashboard, { recordId, flags: { isOutlet } }, WIDGET_DEFINITIONS);
}
export function locationDashboardPlacements(recordId: string) {
  return resolveDashboardPlacements(locationDashboard, { recordId }, WIDGET_DEFINITIONS);
}
export function storageLocationDashboardPlacements(recordId: string) {
  return resolveDashboardPlacements(storageLocationDashboard, { recordId }, WIDGET_DEFINITIONS);
}
export function locationDashboardInputs(recordId: string): Record<string, Record<string, unknown>> {
  return Object.fromEntries(locationDashboardPlacements(recordId).map(item => [item.widgetId, item.inputs ?? {}]));
}
export function branchDefaultDashboardPlacements(isOutlet: boolean) {
  return resolveDashboardPlacements({ ...branchDashboard, queryScope: null }, { flags: { isOutlet } });
}
export function locationDefaultDashboardPlacements() {
  return resolveDashboardPlacements({ ...locationDashboard, queryScope: null });
}
export function storageLocationDefaultDashboardPlacements() {
  return resolveDashboardPlacements({ ...storageLocationDashboard, queryScope: null });
}

export { teamDashboard, departmentDashboard, accessRoleDashboard };

const RECORD_METRIC_DASHBOARDS = [teamDashboard, departmentDashboard, accessRoleDashboard];

/** Owner-authorised metric values are composed into explicit record dashboard placements. */
export function recordSummaryMetrics(values: Readonly<Record<string, number>>): {
  definitions: DashboardWidgetDefinition[];
  placements: DashboardWidgetPlacement[];
  locked: Record<string, Record<string, unknown>>;
} {
  const definitions = WIDGET_DEFINITIONS.filter(widget => Object.prototype.hasOwnProperty.call(values, widget.widgetId));
  const ids = new Set(definitions.map(widget => widget.widgetId));
  const placements = RECORD_METRIC_DASHBOARDS.flatMap(dashboard => resolveDashboardPlacements(dashboard))
    .filter(item => ids.has(item.widgetId));
  return {
    definitions,
    placements,
    locked: Object.fromEntries(definitions.map(widget => [widget.widgetId, { ...widget.inputs, value: values[widget.widgetId] }])),
  };
}
